import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  GraduationCap, Bot, 
  Sparkles, Database, BookOpen, MessageSquare, Target, Users, 
  Zap, ChevronRight, ArrowUpRight, BarChart3, ExternalLink, Building2, ClipboardCheck, Presentation, Calculator, FolderOpen, CreditCard
} from 'lucide-react';
import { DocumentsManagerModal } from '../components/documents/DocumentsManagerModal';
import { motion } from 'framer-motion';
import AtlasAI from '../components/AtlasAI';
import KnowledgeBaseManager from '../components/KnowledgeBaseManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { CARRIER_EAPPS } from '../data/carrierEApps';
import { useDocumentViewer } from '../context/DocumentViewerContext';

// Carrier E-App Shortcuts Dropdown — quick launchpad for carrier e-application sites
const CarrierEAppDropdown = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <motion.button
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        data-testid="carrier-eapp-dropdown-trigger"
        className="group relative w-full text-left p-3 sm:p-4 rounded-2xl border bg-white hover:border-transparent hover:shadow-xl transition-all duration-300"
      >
        <div className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-30 blur-lg transition-opacity duration-300 bg-gradient-to-br from-fuchsia-500 to-rose-600"></div>
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-fuchsia-500 to-rose-600"></div>

        <div className="relative flex items-start gap-3 sm:gap-4">
          <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-600 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm sm:text-base font-semibold text-[#2B3674] group-hover:text-white transition-colors">Carrier E-App Shortcuts</h3>
              <span className="px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider rounded-full bg-fuchsia-100 text-fuchsia-700">
                {CARRIER_EAPPS.length}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-[#A3AED0] group-hover:text-white/80 transition-colors mt-0.5">
              One-click launch to any carrier's e-application portal
            </p>
          </div>
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-[#A3AED0] group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
        </div>
      </motion.button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      className="w-72 max-h-96 overflow-y-auto"
      data-testid="carrier-eapp-dropdown-menu"
    >
      <DropdownMenuLabel className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-fuchsia-600" />
        Select Carrier
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {CARRIER_EAPPS.map((c) => (
        <DropdownMenuItem
          key={c.name}
          onClick={() => window.open(c.url, '_blank', 'noopener,noreferrer')}
          data-testid={`carrier-eapp-item-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className="cursor-pointer flex items-center justify-between gap-2 group"
        >
          <span className="text-sm">{c.name}</span>
          <ExternalLink className="h-3.5 w-3.5 text-[#A3AED0] group-hover:text-fuchsia-600 flex-shrink-0" />
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Tool Card Component with Hover Glow Effect
const ToolCard = ({ icon: Icon, title, description, onClick, gradient, glowColor, badge, disabled }) => (
  <motion.button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={`tool-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    whileHover={disabled ? {} : { scale: 1.02, y: -2 }}
    whileTap={disabled ? {} : { scale: 0.98 }}
    className={`group relative w-full text-left p-3 sm:p-4 rounded-2xl border transition-all duration-300 ${
      disabled 
        ? 'opacity-50 cursor-not-allowed bg-[#F4F7FE] border-[#E9EDF7]'
        : 'bg-white border-[#E9EDF7]/80 hover:border-transparent hover:shadow-xl'
    }`}
  >
    {/* Colored Glow Effect on Hover */}
    {!disabled && (
      <>
        <div className={`absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-30 blur-lg transition-opacity duration-300 ${gradient}`}></div>
        <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${gradient}`}></div>
      </>
    )}
    
    <div className="relative flex items-start gap-3 sm:gap-4">
      <div className={`p-2 sm:p-3 rounded-xl ${disabled ? 'bg-slate-200' : gradient} group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
        <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${disabled ? 'text-[#A3AED0]' : 'text-white'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm sm:text-base font-semibold text-[#2B3674] group-hover:text-white transition-colors">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-700">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm text-[#A3AED0] group-hover:text-white/80 transition-colors mt-0.5">{description}</p>
      </div>
      {!disabled && (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-[#A3AED0] group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
      )}
    </div>
  </motion.button>
);

// Link Card for external resources with Hover Glow
const LinkCard = ({ icon: Icon, title, description, url, gradient, onClick }) => (
  <motion.a
    href={onClick ? undefined : url}
    target={onClick ? undefined : '_blank'}
    rel="noopener noreferrer"
    onClick={onClick ? (e) => { e.preventDefault(); onClick(); } : undefined}
    role={onClick ? 'button' : undefined}
    data-testid={`link-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
    className="group relative block p-3 sm:p-4 rounded-2xl border bg-white hover:border-transparent hover:shadow-xl transition-all duration-300"
  >
    {/* Colored Glow Effect on Hover */}
    <div className={`absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-30 blur-lg transition-opacity duration-300 ${gradient}`}></div>
    <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${gradient}`}></div>
    
    <div className="relative flex items-center gap-2 sm:gap-3">
      <div className={`p-2 sm:p-2.5 rounded-xl ${gradient} group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
        <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-xs sm:text-sm font-medium text-[#2B3674] group-hover:text-white transition-colors truncate">{title}</h3>
        <p className="text-[10px] sm:text-xs text-[#A3AED0] group-hover:text-white/70 transition-colors truncate">{description}</p>
      </div>
      <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4 text-[#A3AED0] group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
    </div>
  </motion.a>
);

export default function Resources() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAtlasAI, setShowAtlasAI] = useState(false);
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const { openDocument } = useDocumentViewer();

  // Render Atlas AI full screen
  if (showAtlasAI) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-white"
      >
        <AtlasAI onBack={() => setShowAtlasAI(false)} />
      </motion.div>
    );
  }

  // Render Knowledge Base full screen
  if (showKnowledgeBase) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-white overflow-y-auto"
      >
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#2447D6] to-blue-600">
                <Database className="h-6 w-6 text-white" />
              </div>
              <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2B3674]">Knowledge Base Management</h1>
            </div>
            <button
              onClick={() => setShowKnowledgeBase(false)}
              className="p-2 rounded-full hover:bg-[#E9EDF7] transition-colors"
            >
              <ChevronRight className="h-5 w-5 rotate-180" />
            </button>
          </div>
          <KnowledgeBaseManager />
        </div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 p-4 sm:p-0">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="h-8 w-1 sm:h-10 sm:w-1.5 bg-gradient-to-b from-[#2447D6] via-blue-500 to-violet-500 rounded-full"></div>
          <div>
            <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2B3674]">Resource Hub</h1>
            <p className="text-xs sm:text-sm text-[#A3AED0] hidden sm:block">Tools, training, and everything you need to succeed</p>
          </div>
        </div>
      </motion.div>

      {/* Atlas AI Hero Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <CardContent className="p-0">
            <div className="relative">
              {/* Animated background */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#2447D6]/20 via-transparent to-violet-500/20"></div>
              <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-[#2447D6]/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 sm:w-64 sm:h-64 bg-violet-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                {/* Atlas AI Button */}
                <button
                  onClick={() => setShowAtlasAI(true)}
                  data-testid="atlas-ai-btn"
                  className="group relative flex-shrink-0 w-full sm:w-auto"
                >
                  {/* Glow rings */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#2447D6] to-blue-500 opacity-75 blur-lg group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#3B82F6] via-blue-500 to-violet-500 opacity-50 blur animate-pulse"></div>
                  
                  {/* Button content */}
                  <div className="relative flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-6 sm:py-5 rounded-2xl bg-gradient-to-r from-[#2447D6] to-blue-600 text-white shadow-2xl shadow-[#2447D6]/30 group-hover:shadow-[#2447D6]/50 transition-all">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Bot className="h-5 w-5 sm:h-7 sm:w-7" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg sm:text-xl font-bold">Atlas AI</span>
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300 animate-pulse" />
                      </div>
                      <p className="text-xs sm:text-sm text-blue-100/90">Your 24/7 AI Assistant</p>
                    </div>
                    <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
                
                {/* Description */}
                <div className="flex-1 text-white">
                  <h2 className="text-base sm:text-lg font-semibold mb-2">Breeze's AI Operating Assistant</h2>
                  <p className="text-xs sm:text-sm text-[#A3AED0] leading-relaxed mb-3 sm:mb-4">
                    Get instant answers about onboarding, sales scripts, underwriting, carrier products, and operations. 
                    Aligned to The Breeze Way.
                  </p>
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    {['Scripts', 'Underwriting', 'Products', 'Training'].map((tag) => (
                      <span key={tag} className="px-2 py-1 text-[10px] sm:text-xs rounded-full bg-white/10 text-white/80">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tools Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-xs sm:text-sm font-semibold text-[#A3AED0] uppercase tracking-wider mb-3 sm:mb-4">Tools & Calculators</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <CarrierEAppDropdown />
          <ToolCard
            icon={Presentation}
            title="Show Options"
            description="Build and present the Three-Option Close to clients"
            onClick={() => navigate('/show-options')}
            gradient="bg-gradient-to-br from-violet-500 to-purple-600"
          />
          <ToolCard
            icon={Target}
            title="Buy Breeze Leads"
            description="Buy exclusive Breeze IUL lead packages with secure Stripe checkout"
            onClick={() => navigate('/leads')}
            gradient="bg-gradient-to-br from-cyan-500 to-blue-600"
            badge="New"
          />
          {user?.role === 'admin' && (
            <ToolCard
              icon={CreditCard}
              title="Lead Store Admin"
              description="Orders, weekly cycles, pricing, purchasing window and refunds"
              onClick={() => navigate('/leads/admin')}
              gradient="bg-gradient-to-br from-slate-700 to-slate-900"
              badge="Admin"
            />
          )}
          <ToolCard
            icon={Calculator}
            title="Commission Calculator"
            description="Estimate first-year comp, 9-month advance and as-earned for IUL, Term & FEX"
            onClick={() => navigate('/commission-calculator')}
            gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          />
          <ToolCard
            icon={GraduationCap}
            title="Product Quiz"
            description="Test your insurance product knowledge"
            onClick={() => navigate('/quiz')}
            gradient="bg-gradient-to-br from-amber-500 to-orange-600"
          />
          <ToolCard
            icon={ClipboardCheck}
            title="Post-Sale Process"
            description="Run your 4-step play after every sale — text, email, gift, portal"
            onClick={() => navigate('/post-sale')}
            gradient="bg-gradient-to-br from-sky-500 to-blue-600"
            badge="New"
          />
          {user?.role === 'admin' && (
            <ToolCard
              icon={FolderOpen}
              title="Documents"
              description="Upload PDFs & files agents can open inside ATLAS"
              onClick={() => setShowDocuments(true)}
              gradient="bg-gradient-to-br from-cyan-500 to-sky-600"
              badge="Admin"
            />
          )}
          <DocumentsManagerModal open={showDocuments} onOpenChange={setShowDocuments} />
          {user?.role === 'admin' && (
            <ToolCard
              icon={Database}
              title="Knowledge Base"
              description="Manage Atlas AI training documents"
              onClick={() => setShowKnowledgeBase(true)}
              gradient="bg-gradient-to-br from-[#2447D6] to-blue-600"
              badge="Admin"
            />
          )}
          {user?.role === 'admin' && (
            <ToolCard
              icon={BarChart3}
              title="Atlas AI Question Log"
              description="View what agents are asking (anonymous)"
              onClick={() => navigate('/atlas-question-log')}
              gradient="bg-gradient-to-br from-indigo-500 to-purple-600"
              badge="Admin"
            />
          )}
        </div>
      </motion.div>

      {/* Quick Links */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-xs sm:text-sm font-semibold text-[#A3AED0] uppercase tracking-wider mb-3 sm:mb-4">Quick Links & Resources</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          <LinkCard
            icon={GraduationCap}
            title="Breeze Training Hub"
            description="Drive Folder"
            url="https://drive.google.com/drive/folders/1hQt8n-yIcpCSwpLzbRFzszNoX7e-EHuJ?usp=drive_link"
            gradient="bg-gradient-to-br from-[#2447D6] to-sky-600"
          />
          <LinkCard
            icon={BookOpen}
            title="Prospecting Playbook"
            description="Master PDF Guide"
            onClick={() => openDocument({ slug: 'prospecting-playbook', title: 'Master Breeze Prospecting Playbook' })}
            gradient="bg-gradient-to-br from-rose-500 to-pink-600"
          />
          <LinkCard
            icon={MessageSquare}
            title="IUL Script"
            description="Interactive Notion Doc"
            url="https://www.notion.so/Breeze-IUL-Script-247423bc43ce8013b4facd3fd4f57f52"
            gradient="bg-gradient-to-br from-[#2447D6] to-blue-600"
          />
          <LinkCard
            icon={Target}
            title="Blitz Training"
            description="Training Matrix"
            url="https://start.blitztrainingmatrix.com/"
            gradient="bg-gradient-to-br from-amber-500 to-orange-600"
          />
          <LinkCard
            icon={Zap}
            title="Insurance ToolKits"
            description="Tools & Resources"
            url="https://insurancetoolkits.com/signup"
            gradient="bg-gradient-to-br from-violet-500 to-purple-600"
          />
          <ToolCard
            icon={Users}
            title="Breeze Leadz"
            description="Purchase Leads"
            onClick={() => navigate('/leads')}
            gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          />
          <LinkCard
            icon={MessageSquare}
            title="Agency Chat"
            description="Telegram Group"
            url="https://t.me/+DfodmWykl5JjOWFh"
            gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
          />
        </div>
      </motion.div>
    </div>
  );
}
